// src/contexts/AuthContext.tsx
// Authentication context provider for global auth state management

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { signIn, signOut, getCurrentUser, confirmSignIn, fetchUserAttributes } from 'aws-amplify/auth';
import { authService } from '../services/authService';
import { logger } from '../utils/logger';

// Create service logger
const authLogger = logger.createServiceLogger('AuthContext');

// User attributes interface
interface UserAttributes {
  email?: string;
  name?: string;
  'custom:role'?: string;
  'custom:tenantId'?: string;
  'custom:tenant_id'?: string;
}

// Auth context state interface
interface AuthContextState {
  // User data
  user: any | null;
  attributes: UserAttributes | null;
  
  // Derived values for easy access
  tenantId: string | null;
  userRole: string | null;
  userId: string | null;
  userName: string | null;
  organizationName: string | null;
  
  // State flags
  loading: boolean;
  error: string | null;
  passwordChangeRequired: boolean;
  
  // Auth methods
  login: (email: string, password: string, expectedTenantId: string) => Promise<boolean>;
  changePassword: (newPassword: string) => Promise<boolean>;
  logout: () => Promise<void>;
  
  // Utility methods
  checkAuthState: () => Promise<void>;
  clearError: () => void;
  setOrganizationName: (name: string) => void;
}

// Create context with undefined default
const AuthContext = createContext<AuthContextState | undefined>(undefined);

// Props for the provider
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Core auth state
  const [user, setUser] = useState<any>(null);
  const [attributes, setAttributes] = useState<UserAttributes | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Additional state
  const [organizationName, setOrganizationNameState] = useState<string | null>(null);
  
  // Password change flow state
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [loginEmail, setLoginEmail] = useState('');

  // Derive commonly used values
  const tenantId = attributes?.['custom:tenantId'] || attributes?.['custom:tenant_id'] || null;
  const userRole = attributes?.['custom:role'] || null;
  const userId = user?.userId || user?.username || null;
  const userName = attributes?.name || attributes?.email || null;

  // Check authentication state
  const checkAuthState = async () => {
    try {
      authLogger.debug('Starting authentication state check');
      const currentUser = await getCurrentUser();
      authLogger.debug('Current user retrieved', { 
        userId: currentUser.userId || currentUser.username,
        userExists: !!currentUser 
      });
      setUser(currentUser);
      
      const userAttributes = await fetchUserAttributes();
      authLogger.debug('User attributes retrieved', { 
        email: userAttributes.email,
        hasCustomAttrs: !!(userAttributes['custom:role'] && (userAttributes['custom:tenantId'] || userAttributes['custom:tenant_id']))
      });
      setAttributes(userAttributes);

      // Store UI state data in localStorage for persistence across page refreshes
      if (currentUser && userAttributes) {
        const tenantId = userAttributes['custom:tenantId'] || userAttributes['custom:tenant_id'];
        authLogger.info('Setting localStorage with user context', {
          tenantId,
          role: userAttributes['custom:role'],
          name: userAttributes.name
        });
        
        localStorage.setItem('tenantId', tenantId || '');
        localStorage.setItem('userRole', userAttributes['custom:role'] || '');
        localStorage.setItem('userId', currentUser.userId || currentUser.username);
        localStorage.setItem('userName', userAttributes.name || userAttributes.email || 'User');
        
        // Load organization name from localStorage if available
        const storedOrgName = localStorage.getItem('organizationName');
        if (storedOrgName) {
          setOrganizationNameState(storedOrgName);
        }
      } else {
        authLogger.error('Missing currentUser or userAttributes during auth state check');
      }
    } catch (err) {
      authLogger.error('Authentication state check failed', undefined, err as Error);
      setUser(null);
      setAttributes(null);
      // Clear UI state on auth failure
      localStorage.removeItem('tenantId');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      localStorage.removeItem('organizationName');
      
      // Clear auth service cache
      authService.clearTokenCache();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthState();
  }, []);

  const login = async (email: string, password: string, expectedTenantId: string) => {
    try {
      setError(null);
      authLogger.info('Starting login process', { email, expectedTenantId });
      
      const result = await signIn({ username: email, password });
      authLogger.debug('SignIn result received', { 
        isSignedIn: result.isSignedIn,
        nextStep: result.nextStep?.signInStep
      });
      
      if (result.isSignedIn) {
        authLogger.info('SignIn successful, validating tenant membership');
        
        // CRITICAL: Validate tenant BEFORE calling checkAuthState
        try {
          const userAttributes = await fetchUserAttributes();
          const userTenantId = userAttributes['custom:tenantId'] || userAttributes['custom:tenant_id'];
          
          authLogger.debug('Tenant validation:', {
            expectedTenantId,
            userTenantId,
            match: userTenantId === expectedTenantId
          });

          if (userTenantId !== expectedTenantId) {
            authLogger.error('Tenant mismatch detected', {
              expected: expectedTenantId,
              actual: userTenantId
            });
            
            // Log user out immediately
            await signOut();
            setError('Your account is not associated with this department');
            return false;
          }

          // SUCCESS - User belongs to correct tenant
          authLogger.info('Tenant validation passed, loading user data');
          await checkAuthState();
          return true;
          
        } catch (attrError) {
          authLogger.error('Failed to validate tenant', undefined, attrError as Error);
          await signOut();
          setError('Failed to validate your account');
          return false;
        }
        
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        authLogger.info('Password change required for user');
        setPasswordChangeRequired(true);
        setTempPassword(password);
        setLoginEmail(email);
        setError('Please set a new password to complete setup');
        return false;
      } else {
        authLogger.warn('SignIn not complete, additional steps required', { 
          nextStep: result.nextStep 
        });
        if (result.nextStep) {
          setError(`Additional step required: ${result.nextStep.signInStep}`);
          return false;
        }
      }
      
      return true;
    } catch (err: any) {
      authLogger.error('Login failed', { email }, err);
      setError(err.message || 'Login failed');
      return false;
    }
  };

  const changePassword = async (newPassword: string) => {
    try {
      setError(null);
      authLogger.info('Starting password change process');
      
      // Send all name attributes to support both Dev and Prod Cognito schemas
      const emailPrefix = loginEmail?.split('@')[0] || 'User';
      const result = await confirmSignIn({ 
        challengeResponse: newPassword,
        options: {
          userAttributes: {
            name: loginEmail || 'User',
            given_name: emailPrefix,
            family_name: emailPrefix
          }
        }
      });
      
      authLogger.debug('Password change result', { isSignedIn: result.isSignedIn });
      
      if (result.isSignedIn) {
        authLogger.info('Password changed successfully and user signed in');
        setPasswordChangeRequired(false);
        setTempPassword('');
        setLoginEmail('');
        await checkAuthState();
        return true;
      } else {
        authLogger.warn('Password change not complete', { nextStep: result.nextStep });
        setError('Password change failed');
        return false;
      }
    } catch (err: any) {
      authLogger.error('Password change failed', undefined, err);
      setError(err.message || 'Password change failed');
      return false;
    }
  };

  const logout = async () => {
    try {
      authLogger.info('Starting logout process');
      await signOut();
      setUser(null);
      setAttributes(null);
      setPasswordChangeRequired(false);
      setTempPassword('');
      setOrganizationNameState(null);
      
      // Clear UI state (no manual token cleanup)
      localStorage.removeItem('tenantId');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      localStorage.removeItem('organizationName');
      
      // Clear auth service cache
      authService.clearTokenCache();
      authLogger.info('Logout completed successfully');
    } catch (err) {
      authLogger.error('Logout process failed', undefined, err as Error);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const setOrganizationName = (name: string) => {
    setOrganizationNameState(name);
    localStorage.setItem('organizationName', name);
  };

  const contextValue: AuthContextState = {
    user,
    attributes,
    tenantId,
    userRole,
    userId,
    userName,
    organizationName,
    loading,
    error,
    passwordChangeRequired,
    login,
    changePassword,
    logout,
    checkAuthState,
    clearError,
    setOrganizationName
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export context for advanced use cases
export { AuthContext };
